import httpService from '@Utils/httpService';

const baseUrl = 'api/tags/ava-inspect';

export default {
  /**
   * Get AVA Inspect tags (TypeHead - lightweight for autocomplete)
   * @param {Object} params - { assetId, search, page, itemsPerPage, sortBy, sortDesc }
   */
  get(params) {
    return httpService.get({
      url: `${baseUrl}/TypeHead`,
      params,
    });
  },

  /**
   * Get a single AVA Inspect tag by ID (full details with includes)
   * @param {string} id - Tag ID
   * @param {string|null} [facilityId] - Current facility scope
   */
  getById(id, facilityId = null) {
    return httpService.get({
      url: `${baseUrl}/${id}`,
      ...(facilityId ? { headers: { 'X-Facility-Id': facilityId } } : {}),
    });
  },

  /**
   * Get tag by ID (lightweight, minimal includes)
   * @param {string} id - Tag ID
   */
  getTagById(id) {
    return httpService.get({
      url: `${baseUrl}/getTagById`,
      params: { id },
    });
  },

  /**
   * Get multiple tags by IDs
   * @param {string[]} ids - Array of tag IDs
   * @param {string} assetId - Facility ID
   * @param {AbortSignal|null} [signal] - Optional AbortSignal for cancellation
   */
  getTagsByIds(ids, assetId, signal = null) {
    return httpService.post({
      url: `${baseUrl}/getTagsByIds`,
      data: { ids, assetId },
      ...(signal ? { signal } : {}),
    });
  },

  /**
   * Search for tags with advanced filtering
   * @param {Object} query - TagSearchQuery object
   * @param {AbortSignal} signal - Optional AbortSignal for cancellation
   */
  search(query, signal = null) {
    return httpService.post({
      url: `${baseUrl}/Search`,
      data: query,
      signal,
    });
  },

  /**
   * Search and return only DbIds (for viewer isolation)
   * @param {Object} query - TagSearchQuery object
   */
  searchDbIds(query) {
    return httpService.post({
      url: `${baseUrl}/SearchDbIds`,
      data: query,
    });
  },

  /**
   * Fetch AVA Inspect tags with dynamic search
   * @param {Object} params - { assetId, searchText64, index, range, allProperties }
   */
  fetchAvaInspect(params) {
    return httpService.get({
      url: `${baseUrl}/fetchAvaInspect`,
      params,
    });
  },

  /**
   * Get all manual (non-master) AVA Inspect tags
   * @param {string} assetId - Facility ID
   */
  getAllManualAvaInspect(assetId) {
    return httpService.get({
      url: `${baseUrl}/getAllManualAvaInspect`,
      params: { assetId },
    });
  },

  /**
   * Get all tags with minimal info (for efficient viewer loading)
   * @param {string} assetId - Facility ID
   */
  getAllTagsMinimalInfo(assetId) {
    return httpService.get({
      url: `${baseUrl}/getAllTagsMinimalInfo`,
      params: { assetId },
    });
  },

  /**
   * Get DbIds of tags matching search
   * @param {string} assetId - Facility ID
   * @param {string} search - Base64-encoded search text
   */
  getDbIds(assetId, search = null) {
    return httpService.get({
      url: `${baseUrl}/${assetId}/dbIds`,
      params: { search },
    });
  },

  /**
   * Create a new AVA Inspect tag
   * @param {Object} data - CreateAvaInspectInfo object
   */
  add(data) {
    return httpService.post({
      url: `${baseUrl}/createAvaInspect`,
      data,
    });
  },

  /**
   * Update an existing AVA Inspect tag
   * @param {Object} data - CreateAvaInspectInfo object with tag.id
   */
  update(data) {
    if (!data.tag?.id) return this.add(data);

    return httpService.post({
      url: `${baseUrl}/updateAvaInspectTag`,
      data,
    });
  },

  /**
   * Update only the Corrosion Analyzer status fields
   * @param {string} id - Tag ID
   * @param {Object} statusData - { analyzerMode, analyzerStatus, analyzerResultsLinkId }
   */
  updateAnalyzerStatus(id, statusData) {
    return httpService.put({
      url: `${baseUrl}/${id}/updateAnalyzerStatus`,
      data: statusData,
    });
  },

  /**
   * Delete one or more AVA Inspect tags
   * @param {string|string[]} ids - Tag ID or array of tag IDs
   */
  del(ids, assetId) {
    const idsArray = Array.isArray(ids) ? ids : [ids];
    return httpService.post({
      url: `${baseUrl}/deleteAvaInspect`,
      data: { ids: idsArray, assetId },
    });
  },

  /**
   * Get deletion impact analysis for one or more AVA Inspect tags
   * @param {string|string[]} ids - Tag ID or array of tag IDs
   * @returns {Object} TagDeletionImpactVM with categories and counts
   */
  getDeletionImpact(ids, assetId) {
    const idsArray = Array.isArray(ids) ? ids : [ids];
    return httpService.post({
      url: `${baseUrl}/deletionImpact`,
      data: { ids: idsArray, assetId },
    });
  },

  /**
   * Get the saved crop volume for a tag
   * @param {string} id - Tag ID
   * @returns {Object} { hasCrop: boolean, cropVolume?: { Min, Max } }
   */
  getCropVolume(id) {
    return httpService.get({
      url: `${baseUrl}/${id}/crop`,
    });
  },

  /**
   * Calculate height from deck for a given point
   * @param {string} assetId - Facility ID
   * @param {Object} point - { x, y, z }
   * @param {number|null} deckElevation - Optional manual deck elevation override
   * @returns {Object} { heightFromDeck, deckBelowElevation, detectedLevel }
   */
  calculateHeight(assetId, point, deckElevation = null) {
    return httpService.post({
      url: `${baseUrl}/calculateHeight`,
      data: {
        assetId,
        tagPoint: { X: point.x, Y: point.y, Z: point.z },
        deckElevationOverride: deckElevation,
      },
    });
  },

  /**
   * Get available decks/levels for height calculation
   * @param {string} assetId - Facility ID
   * @returns {Array} [{ name, elevation }, ...]
   */
  getAvailableDecks(assetId) {
    return httpService.get({
      url: `${baseUrl}/${assetId}/availableDecks`,
    });
  },

  /**
   * Get or preview settings for AVA Inspect tag ID generation
   * @param {string} assetId - Facility ID
   * @returns {Object} { assetId, clientPrefix, facilityCode, currentSequence, nextTagIdPreview }
   */
  getSettings(assetId) {
    return httpService.get({
      url: `${baseUrl}/settings/${assetId}`,
    });
  },

  /**
   * Update or initialize settings for a facility
   * @param {string} assetId - Facility ID
   * @param {Object} settings - { clientPrefix, facilityCode }
   * @returns {Object} Updated settings with nextTagIdPreview
   */
  updateSettings(assetId, settings) {
    return httpService.put({
      url: `${baseUrl}/settings/${assetId}`,
      data: settings,
    });
  },
};
